import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Modal,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import { X, RefreshCw, Info, FileText, Trash2 } from 'lucide-react';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '80%',
  maxWidth: 900,
  maxHeight: '80vh',
  bgcolor: 'background.paper',
  boxShadow: 24,
  borderRadius: 1,
  p: 4,
  overflow: 'auto',
};

const EmailHistoryModal = ({ open, onClose, user, accessToken }) => {
  const [emailLogs, setEmailLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState({});
  const [deleting, setDeleting] = useState({});
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedEmailType, setSelectedEmailType] = useState(null);
  const [deleteConfirmDialogOpen, setDeleteConfirmDialogOpen] = useState(false);
  const [emailToDelete, setEmailToDelete] = useState(null);
  const [autoRefreshing, setAutoRefreshing] = useState(false);
  const initialLoadDoneRef = useRef(false);

  useEffect(() => {
    if (open && user) {
      // Reset the flag when modal is opened
      initialLoadDoneRef.current = false;
      fetchEmailHistory();
    }
  }, [open, user]);

  useEffect(() => {
    // Only autorefresh once after initial load and when not already refreshing
    if (open && emailLogs.length > 0 && !loading && !autoRefreshing && !initialLoadDoneRef.current) {
      initialLoadDoneRef.current = true; // Mark that we've done the initial refresh
      autoRefreshEmailStatuses();
    }
  }, [loading, emailLogs.length, open, autoRefreshing]);

  const fetchEmailHistory = async () => {
    if (!user || !user.email) return;

    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/admin/user-email-history?email=${encodeURIComponent(user.email)}`,
        {
          credentials: 'include',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        },
      );

      if (!response.ok) {
        throw new Error('Failed to fetch email history');
      }

      const data = await response.json();
      setEmailLogs(data.emailLogs || []);
    } catch (error) {
      console.error('Error fetching email history:', error);
    } finally {
      setLoading(false);
    }
  };

  const autoRefreshEmailStatuses = async () => {
    if (emailLogs.length === 0) return;

    setAutoRefreshing(true);

    const refreshingState = emailLogs.reduce((state, log) => {
      state[log.id] = true;
      return state;
    }, {});

    setRefreshing(refreshingState);

    try {
      const refreshPromises = emailLogs.map(async (log, index) => {
        try {
          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/admin/email-status/${log.id}`, {
            credentials: 'include',
            headers: {
              Authorization: `Bearer ${accessToken}`,
              'Content-Type': 'application/json',
            },
          });

          if (!response.ok) {
            throw new Error(`Failed to refresh email status for log ID ${log.id}`);
          }

          const data = await response.json();
          return { index, data };
        } catch (error) {
          console.error(`Error refreshing email status for log ID ${log.id}:`, error);
          return { index, error };
        } finally {
          setRefreshing((prev) => ({ ...prev, [log.id]: false }));
        }
      });

      const results = await Promise.all(refreshPromises);

      // Update all email logs at once with the results
      setEmailLogs((prevLogs) => {
        const newLogs = [...prevLogs];

        results.forEach((result) => {
          if (result.data && !result.error) {
            const { index, data } = result;
            if (newLogs[index]) {
              newLogs[index] = {
                ...newLogs[index],
                deliveryStatus: data.deliveryStatus,
                statusDetails: data.statusDetails,
                templateHtml: data.templateHtml,
                updatedAt: data.updatedAt,
              };
            }
          }
        });

        return newLogs;
      });

    } catch (error) {
      console.error('Error during auto-refresh of email statuses:', error);
    } finally {
      setAutoRefreshing(false);
    }
  };

  const refreshEmailStatus = async (emailLogId, index) => {
    setRefreshing((prev) => ({ ...prev, [emailLogId]: true }));

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/admin/email-status/${emailLogId}`, {
        credentials: 'include',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to refresh email status');
      }

      const data = await response.json();

      setEmailLogs((prevLogs) => {
        const newLogs = [...prevLogs];
        newLogs[index] = {
          ...newLogs[index],
          deliveryStatus: data.deliveryStatus,
          statusDetails: data.statusDetails,
          templateHtml: data.templateHtml,
          updatedAt: data.updatedAt,
        };
        return newLogs;
      });
    } catch (error) {
      console.error('Error refreshing email status:', error);
    } finally {
      setRefreshing((prev) => ({ ...prev, [emailLogId]: false }));
    }
  };

  const openTemplateDialog = (template, emailType) => {
    setSelectedTemplate(template);
    setSelectedEmailType(emailType);
    setTemplateDialogOpen(true);
  };

  const closeTemplateDialog = () => {
    setTemplateDialogOpen(false);
    setSelectedTemplate(null);
    setSelectedEmailType(null);
  };

  const openDeleteConfirmDialog = (emailLog) => {
    setEmailToDelete(emailLog);
    setDeleteConfirmDialogOpen(true);
  };

  const closeDeleteConfirmDialog = () => {
    setDeleteConfirmDialogOpen(false);
    setEmailToDelete(null);
  };

  const deleteEmailLog = async () => {
    if (!emailToDelete) return;

    setDeleting((prev) => ({ ...prev, [emailToDelete.id]: true }));

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/admin/email-log/${emailToDelete.id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete email log');
      }

      setEmailLogs((prevLogs) => prevLogs.filter((log) => log.id !== emailToDelete.id));

      closeDeleteConfirmDialog();
    } catch (error) {
      console.error('Error deleting email log:', error);
    } finally {
      setDeleting((prev) => ({ ...prev, [emailToDelete.id]: false }));
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const options = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    };
    return date.toLocaleString('en-US', options);
  };

  const getStatusColor = (status) => {
    const statusEvent = typeof status === 'string' ? status : status?.event || 'queued';

    switch (statusEvent) {
      case 'delivered':
      case 'opened':
      case 'clicked':
        return '#2E7D32'; // Green
      case 'failed':
      case 'rejected':
      case 'bounced':
        return '#C62828'; // Red
      case 'accepted':
        return '#1976D2'; // Blue
      default:
        return '#F57F17'; // Orange for queued
    }
  };

  const getStatusText = (status) => {
    const statusEvent = typeof status === 'string' ? status : status?.event || 'queued';

    switch (statusEvent) {
      case 'delivered':
        return 'Delivered';
      case 'opened':
        return 'Opened';
      case 'clicked':
        return 'Clicked';
      case 'failed':
        return 'Failed';
      case 'rejected':
        return 'Rejected';
      case 'bounced':
        return 'Bounced';
      case 'accepted':
        return 'Accepted';
      default:
        return 'Queued';
    }
  };

  const getCompanyName = (emailType) => {
    if (!emailType) return '';

    const parts = emailType.split('-');
    if (parts.length > 0) {
      return parts[0].charAt(0).toUpperCase() + parts[0].slice(1);
    }
    return '';
  };

  const processTemplateHtml = (html, emailType) => {
    if (!html) return '';

    const companyName = getCompanyName(emailType);
    return html.replace(/\{\{\s*client_name\s*\}\}/g, companyName);
  };

  return (
    <>
      <Modal open={open} onClose={onClose}>
        <Box sx={modalStyle}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5" component="h2">
              {user ? `Email History for ${user.name} (${user.email})` : 'Email History'}
            </Typography>
            <IconButton onClick={onClose} size="small">
              <X />
            </IconButton>
          </Box>

          {loading || autoRefreshing ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
              <CircularProgress size={24} />
              {autoRefreshing && (
                <Typography variant="body2" sx={{ ml: 2 }}>
                  Auto-refreshing email statuses...
                </Typography>
              )}
            </Box>
          ) : emailLogs.length === 0 ? (
            <Typography>No email history found for this user.</Typography>
          ) : (
            <TableContainer component={Paper} sx={{ maxHeight: '60vh' }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Sent Date</TableCell>
                    <TableCell>Email Type</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Details</TableCell>
                    <TableCell>Last Updated</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {emailLogs.map((log, index) => (
                    <TableRow key={log.id}>
                      <TableCell>{formatDate(log.sentAt)}</TableCell>
                      <TableCell>{log.emailType}</TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusText(log.statusDetails?.event || log.deliveryStatus)}
                          sx={{
                            bgcolor: `${getStatusColor(log.statusDetails?.event || log.deliveryStatus)}20`,
                            color: getStatusColor(log.statusDetails?.event || log.deliveryStatus),
                            fontWeight: 'bold',
                          }}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {log.statusDetails ? (
                          <Tooltip
                            title={
                              <React.Fragment>
                                <Typography variant="body2">Event: {log.statusDetails.event || 'N/A'}</Typography>
                                <Typography variant="body2">
                                  Time: {formatDate(log.statusDetails.timestamp) || 'N/A'}
                                </Typography>
                                <Typography variant="body2">
                                  Message: {log.statusDetails.statusMessage || 'N/A'}
                                </Typography>
                              </React.Fragment>
                            }
                          >
                            <IconButton size="small">
                              <Info size={16} />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          'No details'
                        )}
                      </TableCell>
                      <TableCell>{formatDate(log.updatedAt)}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          {refreshing[log.id] ? (
                            <CircularProgress size={20} />
                          ) : (
                            <Tooltip title="Refresh status">
                              <IconButton size="small" onClick={() => refreshEmailStatus(log.id, index)}>
                                <RefreshCw size={16} />
                              </IconButton>
                            </Tooltip>
                          )}
                          {log.templateHtml && (
                            <Tooltip title="View Email Template">
                              <IconButton
                                size="small"
                                onClick={() => openTemplateDialog(log.templateHtml, log.emailType)}
                              >
                                <FileText size={16} />
                              </IconButton>
                            </Tooltip>
                          )}
                          {deleting[log.id] ? (
                            <CircularProgress size={20} />
                          ) : (
                            <Tooltip title="Delete Email Log">
                              <IconButton
                                size="small"
                                onClick={() => openDeleteConfirmDialog(log)}
                                sx={{ color: '#d32f2f' }}
                              >
                                <Trash2 size={16} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Modal>

      <Dialog
        open={templateDialogOpen}
        onClose={closeTemplateDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { maxHeight: '95vh', height: '95vh' },
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Email Template Content</Typography>
            <IconButton onClick={closeTemplateDialog} size="small">
              <X size={18} />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', height: 'calc(95vh - 120px)' }}>
          {selectedTemplate ? (
            <Box sx={{ overflow: 'auto', display: 'flex', flexDirection: 'column', height: '100%' }}>
              <Box
                sx={{
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  p: 2,
                  mb: 2,
                  maxHeight: '250px',
                  overflow: 'auto',
                  flex: '0 0 auto',
                }}
              >
                <Typography variant="subtitle2" gutterBottom>
                  Raw HTML
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    bgcolor: '#f5f5f5',
                    p: 2,
                    borderRadius: 1,
                    fontSize: '12px',
                    maxHeight: '200px',
                    overflow: 'auto',
                  }}
                >
                  {selectedTemplate}
                </Box>
              </Box>
              <Box
                sx={{
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  p: 2,
                  overflow: 'auto',
                  bgcolor: '#fff',
                  flex: '1 1 auto',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Typography variant="subtitle2" gutterBottom>
                  Rendered Preview (with {'{{ client_name }}'} replaced)
                </Typography>
                <Box
                  sx={{
                    overflow: 'auto',
                    flex: '1 1 auto',
                    border: '1px solid #f0f0f0',
                    borderRadius: 1,
                    p: 1,
                  }}
                  dangerouslySetInnerHTML={{ __html: processTemplateHtml(selectedTemplate, selectedEmailType) }}
                />
              </Box>
            </Box>
          ) : (
            <Typography>No template content available</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeTemplateDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteConfirmDialogOpen} onClose={closeDeleteConfirmDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this email log?
            {emailToDelete && (
              <>
                <Box component="br" />
                <Box component="strong">Type:</Box> {emailToDelete.emailType}
                <Box component="br" />
                <Box component="strong">Sent:</Box> {formatDate(emailToDelete.sentAt)}
              </>
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteConfirmDialog}>Cancel</Button>
          <Button
            onClick={deleteEmailLog}
            variant="contained"
            color="error"
            disabled={Object.values(deleting).some(Boolean)}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EmailHistoryModal;
